import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_REORDER_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridCellModes,
  type GridCellModesModel,
  type GridCellParams,
  type GridEventListener,
  type GridRowId,
} from '@mui/x-data-grid-pro';
import * as React from 'react';
import { useFlag } from '@/common/hooks/feature-flags/useFlag';
import { EXTERNALLY_WRITABLE_FIELDS } from '../columns/externallyWritableFields';
import type { ItemsTableRow } from '../types';

const initialState: GridCellModesModel = {};

type ColDefWithRowPredicate = {
  isCellEditableForRow?: (row: ItemsTableRow) => boolean;
  editableForExternalItems?: boolean;
};

export const checkCellEditable = (
  params: GridCellParams<ItemsTableRow>,
  readOnly?: boolean,
  integrationItemsEditable?: boolean,
) => {
  if (!params.colDef.editable || readOnly) {
    return false;
  }

  const editableForExternalItems =
    'editableForExternalItems' in params.colDef
      ? (params.colDef as ColDefWithRowPredicate).editableForExternalItems
      : undefined;

  if (params.row.externalKey && !editableForExternalItems) {
    return EXTERNALLY_WRITABLE_FIELDS.has(params.field) || Boolean(integrationItemsEditable);
  }

  const isCellEditableForRow =
    'isCellEditableForRow' in params.colDef
      ? (params.colDef as ColDefWithRowPredicate).isCellEditableForRow
      : undefined;
  if (isCellEditableForRow && !isCellEditableForRow(params.row)) {
    return false;
  }

  return true;
};

export const useItemsTableCell = (readOnly?: boolean) => {
  const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>(initialState);
  const timeoutsRef = React.useRef<Set<NodeJS.Timeout>>(new Set());
  const integrationItemsEditable = useFlag('roadmapsIntegrationItemsEditable');

  const startCellEditMode = React.useCallback((rowId: GridRowId, field: string) => {
    setCellModesModel((prevModel) => {
      return {
        // Revert the mode of the other cells from other rows
        ...Object.fromEntries(
          Object.entries(prevModel).map(([id, fields]) => [
            id,
            Object.fromEntries(Object.keys(fields).map((f) => [f, { mode: GridCellModes.View }])),
          ]),
        ),
        [rowId]: {
          // Revert the mode of other cells in the same row
          ...Object.fromEntries(
            Object.keys(prevModel[rowId] ?? {}).map((f) => [f, { mode: GridCellModes.View }]),
          ),
          [field]: { mode: GridCellModes.Edit },
        },
      };
    });
  }, []);

  const isCellEditable = React.useCallback(
    (params: GridCellParams<ItemsTableRow>) =>
      checkCellEditable(params, readOnly, integrationItemsEditable),
    [readOnly, integrationItemsEditable],
  );

  const onCellClick = React.useCallback<GridEventListener<'cellClick'>>(
    (params: GridCellParams<ItemsTableRow>) => {
      if (
        params.field === GRID_CHECKBOX_SELECTION_COL_DEF.field ||
        params.field === GRID_TREE_DATA_GROUPING_FIELD ||
        params.field === GRID_REORDER_COL_DEF.field
      ) {
        return;
      }

      if (!isCellEditable(params)) {
        return;
      }

      if (params.cellMode !== GridCellModes.Edit) {
        startCellEditMode(params.id, params.field);
      }
    },
    [startCellEditMode, isCellEditable],
  );

  const onCellEditStop = React.useCallback<GridEventListener<'cellEditStop'>>(
    ({ id, field }, event, { api }) => {
      event.defaultMuiPrevented = true;

      const timeoutId = setTimeout(() => {
        timeoutsRef.current.delete(timeoutId);

        if (!api?.getCellMode || !api?.stopCellEditMode) {
          return;
        }

        if (id && field && api.getCellMode(id, field) === GridCellModes.Edit) {
          api.stopCellEditMode({ id, field, ignoreModifications: true });
        }
      }, 50);

      timeoutsRef.current.add(timeoutId);
    },
    [],
  );

  React.useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current.clear();
    };
  }, []);

  // Derived from `cellModesModel` so there's nothing to keep in sync. Exposed so callers can avoid
  // reordering rows while the user is typing.
  const editingRowId = React.useMemo(() => {
    for (const [rowId, fields] of Object.entries(cellModesModel)) {
      if (Object.values(fields).some((cell) => cell.mode === GridCellModes.Edit)) {
        const id = Number(rowId);
        return Number.isNaN(id) ? null : id;
      }
    }
    return null;
  }, [cellModesModel]);

  return {
    editingRowId,
    cellModesModel,
    onCellModesModelChange: setCellModesModel,
    onCellClick,
    onCellEditStop,
    isCellEditable,
  };
};
